import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { AppConstants } from "../../app.constants";
import { Observable, of } from "rxjs";
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ErrorHandlerService } from "src/app/core/services/error-handler.service";

export interface EventMaster {
  _id?: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class LitigationDashboardService {

  constructor(private http: HttpClient, private router: Router, private errorHandlerService: ErrorHandlerService) { }

  getEmailLitigationLogData(email): Observable<any> {
    const params = new HttpParams()
      .set('user_email', email.toString());
    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.LITIGATION_DATA}`, { params }
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getEmailLitigationDataPaged(params: {
    user_email: string;
    selected_option: string;
    selected_status: string | null;
    start_date?: string;
    end_date?: string;
    search?: string;
    page?: number;
    page_size?: number;
    sort_field?: string | null;   // e.g., "Field ID" | "Termination Date" | "Case Status"
    sort_order?: number | null;   // 1 or -1
    selected_event_id?: string;
    search_scope?: string;
    deleted_scope?: string;
  }) {
    let qp = new HttpParams()
      .set('user_email', params.user_email)
      .set('selected_option', params.selected_option)
      .set('page', String(params.page ?? 1))
      .set('page_size', String(params.page_size ?? 10));

    if (params.selected_status) qp = qp.set('selected_status', params.selected_status);
    if (params.start_date) qp = qp.set('start_date', params.start_date);
    if (params.end_date) qp = qp.set('end_date', params.end_date);
    if (params.search?.trim()) qp = qp.set('search', params.search.trim());
    if (params.sort_field?.trim()) {
      qp = qp.set('sort_field', params.sort_field.trim());
    }
    if (typeof params.sort_order === 'number' && (params.sort_order === 1 || params.sort_order === -1)) {
      qp = qp.set('sort_order', String(params.sort_order));
    }
    if (params.selected_event_id) {
      qp = qp.set('selected_event_id', params.selected_event_id); // ✅ pass "all" or specific id
    }
    if (params.search_scope) {
      qp = qp.set('search_scope', params.search_scope);
    }
    if (params.deleted_scope) {
      qp = qp.set('deleted_scope', params.deleted_scope);
    }
    // return this.http.get<{ data: any[]; total: number }>(
    //   'https://portfoliodev.pharsights.com/pm_backend/info/extract_litigation_data/',
    //   { params: qp }
    // );

    return this.http.get(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.LITIGATION_DATA}`, { params: qp }
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  DeleteLitigationLogData(data): Observable<any[]> {
    return this.http.post<any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.DELETE_LITIGATION}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  LitigationApproveReject(data): Observable<any[]> {
    return this.http.post<any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.LITIGATION_AR}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  LitigationOnHold(data): Observable<any[]> {
    return this.http.post<any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.HOLD_LITIGATION}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  AddLitigationLogData(data): Observable<any> {
    return this.http.post<any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.ADD_LITIGATION}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  saveLitigationLogData(data): Observable<any> {
    return this.http.put<any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.UPDATE_LITIGATION}`,
      data
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getAllEvents(): Observable<{ data: EventMaster[] } | EventMaster[]> {
    return this.http.get<{ data: EventMaster[] } | EventMaster[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.EVENTS_GET_ALL}`
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  /** POST: add new event name */
  addNewEvent(eventText: string, createdBy: string): Observable<any> {
    const body = {
      event_text: eventText,
      created_by: createdBy
    };
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.EVENTS_ADD_NEW}`,
      body
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  updateEventName(eventId: string | number, newText: string, updatedBy: string) {
    const body = {
      event_id: String(eventId),
      new_event_text: newText,
      updated_by: updatedBy
    };
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.EVENTS_UPDATE_NAME}`,
      body
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }


  addCaseEvent(formData: FormData): Observable<any> {
    // Do NOT set Content-Type for FormData
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.CASE_EVENTS_ADD}`, formData
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  viewCaseEvents(caseId: string): Observable<{ data: any[] } | any[]> {
    const params = new HttpParams().set('_id', caseId);
    return this.http.get<{ data: any[] } | any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.CASE_EVENTS_VIEW}`, { params }
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  undoDeletedRecord(payload: any) {
    return this.http.post<any>(`${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.UNDO_DELETED_RECORD}`,
      payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  deleteCaseEvent(payload: any) {
    return this.http.post<any>(`${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.CASE_EVENTS_DELETE}`,
      payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  addAppealCase(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.ADD_APPEAL_CASE}`, formData
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  updateAppealCase(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.UPDATE_APPEAL_CASE}`, formData
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  deleteAppealCase(payload: any): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.DELETE_APPEAL_CASE}`, payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  undoAppealCase(payload: any): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.UNDO_APPEAL_CASE}`, payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  getAllAppealEvents(): Observable<{ data: EventMaster[] } | EventMaster[]> {
    return this.http.get<{ data: EventMaster[] } | EventMaster[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.GET_ALL_APPEAL_EVENTS}`
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  addAppealEvent(eventText: string, createdBy: string): Observable<any> {
    const body = {
      event_text: eventText,
      created_by: createdBy
    };
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.ADD_APPEAL_EVENT}`,
      body
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  renameAppealEvent(eventId: string | number, newText: string, updatedBy: string) {
    const body = {
      event_id: Number(eventId),
      new_event_text: newText,
      updated_by: updatedBy
    };
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.RENAME_APPEAL_EVENT}`,
      body
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  addAppealCaseEvent(formData: FormData): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.APPEAL_CASE_EVENTS_ADD}`, formData
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  viewAppealCaseEvents(caseId: string, appealCaseNumber: string): Observable<{ data: any[] } | any[]> {
    const params = new HttpParams()
      .set('_id', caseId)
      .set('appeal_case_number', appealCaseNumber);
    return this.http.get<{ data: any[] } | any[]>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.APPEAL_CASE_EVENTS_VIEW}`, { params }
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  deleteAppealCaseEvent(payload: any) {
    return this.http.post<any>(`${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.APPEAL_CASE_EVENTS_DELETE}`,
      payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }

  appealApproveReject(payload: any): Observable<any> {
    return this.http.post<any>(
      `${AppConstants.API_ENDPOINT}${AppConstants.API.EMAIL_LITIGATION.APPEAL_APPROVE_REJECT}`,
      payload
    ).pipe(catchError((error) => this.errorHandlerService.handleError(error)));
  }
}